/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable consistent-return */
import { config } from '@brands/config/config';
import Container from '@brands/Components/Container/Container';
import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import ConfirmPasswordModal from '@brands/Components/Modals/ConfirmPasswordModal/ConfirmPasswordModal';
import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import { getMfaStatus } from '@brands/services/auth/getMfaStatus';
import { manageMfa } from '@brands/services/auth/manageMfa';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { deletePasskey, listPasskeys, PasskeySummary, registerPasskey } from '@brands/services/auth/webauthn';
import { updateMe } from '@brands/services/identity/updateMe';
import { selectAuth } from '@brands/store/selectors/auth';
import { setNotificationSettings } from '@brands/store/slices/authSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { getPrimaryColor } from '@brands/Utils/getPrimaryColor';
import { Button, Switch } from '@mui/material';
import zIndex from '@mui/material/styles/zIndex';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import cn from 'classnames';
import React, { FC, useEffect, useMemo } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import {
  MdDeleteOutline,
  MdKeyboardArrowLeft,
  MdLockOutline,
  MdOutlineMailOutline,
  MdOutlinePhoneIphone,
} from 'react-icons/md';
import Modal from 'react-modal';
import { toast } from 'react-toastify';

import { useWindowSize } from '../../../../hooks';
import { customStylesDashboard } from '../../../../Utils/customStyles';
import styles from './styles.module.scss';

interface AccountSettingsModalProps {
  isOpen: boolean;
  toggleModal: (bool: boolean) => void;
  onDeleteAccount: () => void;
  onChangePassword: () => void;
  onChangeEmail: () => void;
}

type MfaOptions = {
  value: string;
  label: string;
  disabled?: boolean;
};

const customStylesDashboardCustom = (opacity = '1') => ({
  overlay: {
    ...customStylesDashboard.overlay,
  },
  content: {
    ...customStylesDashboard.content,
    opacity,
  },
});

const AccountSettingsModal: FC<AccountSettingsModalProps> = ({
  isOpen,
  toggleModal,
  onDeleteAccount,
  onChangePassword,
  onChangeEmail,
}) => {
  const isMobile = useWindowSize().width < 768;
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector(selectAuth);

  const isStaffPortalUser = useMemo(
    () =>
      [UserRoleName.Provider, UserRoleName.Collector, UserRoleName.CareAdvisor].includes(
        userInfo?.role?.name as UserRoleName,
      ),
    [userInfo?.role?.name],
  );

  const switchBlueSx = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: '#2d68a6' },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2d68a6', opacity: 0.55 },
  };

  // Notification settings state
  const [loadingState, setLoadingState] = React.useState(false);
  const [noNotificationError, setNoNotificationError] = React.useState(false);

  // Account Security (MFA) state
  const [mfaLoading, setMfaLoading] = React.useState<boolean>(false);
  const [showConfirmPasswordModal, setShowConfirmPasswordModal] = React.useState<boolean>(false);
  const [buttonNumber, setButtonNumber] = React.useState<number>(0);
  const [pendingMfaToggle, setPendingMfaToggle] = React.useState<number | null>(null); // remember desired toggle from modal
  const [isSelected, setIsSelected] = React.useState<number | null>(null); // 1=On, 2=Off
  const [selectedMethod, setSelectedMethod] = React.useState<string>('');
  const [mfaOptionsList, setMfaOptionsList] = React.useState<MfaOptions[]>([{ value: 'EMAIL_MFA', label: 'Email' }]);
  const [initialValues, setInitialValues] = React.useState<{ isSelected: number | null; selected_method: string }>({
    isSelected: null,
    selected_method: '',
  });

  // Passkeys (WebAuthn) state
  const [passkeys, setPasskeys] = React.useState<PasskeySummary[]>([]);
  const refreshPasskeys = React.useCallback(async () => {
    try {
      setPasskeys(await listPasskeys());
    } catch {
      /* not fatal for the settings view */
    }
  }, []);
  React.useEffect(() => {
    if (isOpen && config.features.passkey && browserSupportsWebAuthn()) refreshPasskeys();
  }, [isOpen, refreshPasskeys]);

  const onAddPasskey = async (): Promise<void> => {
    try {
      await registerPasskey();
      await refreshPasskeys();
    } catch {
      /* user cancelled or unsupported */
    }
  };

  const onRemovePasskey = async (id: number): Promise<void> => {
    try {
      await deletePasskey(id);
      await refreshPasskeys();
    } catch (err) {
      displayErrorDetails(err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else if (document.body.classList.contains('modal-open') && !isOpen) {
      document.body.classList.remove('modal-open');
    }
  }, [isOpen]);

  // Initialize MFA options & status
  useEffect(() => {
    (async () => {
      if (!userInfo?.id) return;
      setMfaLoading(true);

      // Delivery methods: show SMS only if phone is present
      if (userInfo.phone_number) {
        setMfaOptionsList([
          { value: 'SMS_MFA', label: 'SMS' },
          { value: 'EMAIL_MFA', label: 'Email' },
        ]);
      } else {
        setMfaOptionsList([{ value: 'EMAIL_MFA', label: 'Email' }]);
      }

      try {
        const status = await getMfaStatus(Number(userInfo.id));
        const hasMfa =
          Array.isArray(status.mfa_method) &&
          (status.mfa_method.includes('SMS_MFA') || status.mfa_method.includes('EMAIL_MFA'));

        setIsSelected(hasMfa ? 1 : 2);

        if (Array.isArray(status.mfa_method) && status.mfa_method.length > 0) {
          setSelectedMethod(status.mfa_method[0]);
        } else {
          setSelectedMethod('EMAIL_MFA');
        }

        setInitialValues({
          isSelected: hasMfa ? 1 : 2,
          selected_method: Array.isArray(status.mfa_method) ? status.mfa_method[0] || '' : '',
        });
      } catch (e) {
        displayErrorDetails(e);
      } finally {
        setMfaLoading(false);
      }
    })();
  }, [userInfo?.id]);

  // After Patient confirms in modal and `isSelected` changes to pending value, autosend API
  useEffect(() => {
    const applyIfPending = async (): Promise<void> => {
      if (pendingMfaToggle === null) return;
      if (isSelected !== pendingMfaToggle) return;

      try {
        setMfaLoading(true);
        await manageMfa(Number(userInfo.id), {
          selected_method: isSelected === 1 ? selectedMethod || 'EMAIL_MFA' : null,
        });
        setInitialValues({
          isSelected,
          selected_method: isSelected === 1 ? selectedMethod || 'EMAIL_MFA' : '',
        });
      } catch (err) {
        displayErrorDetails(err);
        // revert UI if call fails
        setIsSelected(initialValues.isSelected);
        setSelectedMethod(initialValues.selected_method || 'EMAIL_MFA');
      } finally {
        setPendingMfaToggle(null);
        setMfaLoading(false);
      }
    };
    applyIfPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected, pendingMfaToggle]);

  const onCloseConfirmPasswordModal = (): void => {
    setShowConfirmPasswordModal(false);
  };

  // Notification settings
  const updateNotificationSettings = async (smsOptIn: boolean, emailOptIn: boolean): Promise<void> => {
    if (!smsOptIn && !emailOptIn) {
      setNoNotificationError(true);
      return;
    }

    try {
      setLoadingState(true);
      setNoNotificationError(false);
      await dispatch(setNotificationSettings({ sms: smsOptIn, email: emailOptIn }));
      await updateMe(
        {
          email_opt_in: emailOptIn,
          sms_opt_in: smsOptIn,
        },
        Number(userInfo.id),
        userInfo.role?.name,
        userInfo.role?.name === UserRoleName.Collector ? { collectorEditingSelf: true } : undefined,
      );
      toast.success('Notification preferences saved.', { toastId: 'notification-preferences-saved' });
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setLoadingState(false);
    }
  };

  const closeModal = (): void => {
    toggleModal(false);
  };

  // ---- MOBILE LAYOUT ----
  if (isMobile) {
    return (
      <Container
        patientDashboard
        isSidenavVisible
        childClassName={styles.accountSettingsDashboard}
        containerStyle={{
          justifyContent: 'start',
          overflowY: 'scroll',
          zIndex: zIndex.drawer + 1,
        }}
      >
        <div className={styles.accountSettingsDashboardContainer}>
          <div
            className={cn(styles.accountSettingsCard)}
          >
            {(!isStaffPortalUser || isMobile) && (
              <button type="button" className={styles.backButton} onClick={closeModal} aria-label="Back">
                <MdKeyboardArrowLeft />
                Back
              </button>
            )}

            {loadingState || mfaLoading ? (
              <Loading dotted />
            ) : (
              <div className={styles.accountSettingsContainer}>
                <h1 className={`fs-unmask ${styles.accountSettingsTitle}`}>Account settings</h1>

                {/* Login email */}
                <div className={styles.accountSettingsLoginEmail}>
                  <div className={styles.accountSettingsLoginEmailContainer}>
                    <div className={`fs-unmask ${styles.accountSettingsLoginEmailLabel}`}>Login email</div>
                    <div className={`fs-exclude ${styles.accountSettingsLoginEmailValue}`}>{userInfo?.email}</div>
                  </div>
{/*                   <div className={styles.accountSettingsLoginEmailEdit}>
                    <button
                      type="button"
                      className={styles.accountSettingsLoginEmailEditButton}
                      onClick={onChangeEmail}
                    >
                      Edit
                    </button>
                  </div> */}
                </div>

                {/* Password */}
                <div className={styles.accountSettingsLoginEmail}>
                  <div className={styles.accountSettingsLoginEmailContainer}>
                    <div className={`fs-unmask ${styles.accountSettingsLoginEmailLabel}`}>Password</div>
                    <div className={`fs-unmask ${styles.accountSettingsLoginEmailValue}`}>********</div>
                  </div>
                  <div className={styles.accountSettingsLoginEmailEdit}>
                    <button
                      type="button"
                      className={styles.accountSettingsLoginEmailEditButton}
                      onClick={onChangePassword}
                    >
                      Edit
                    </button>
                  </div>
                </div>

                {/* Passkeys */}
                {config.features.passkey && browserSupportsWebAuthn() && (
                  <div className={styles.accountSettingsLoginEmail}>
                    <div className={styles.accountSettingsLoginEmailContainer}>
                      <div className={`fs-unmask ${styles.accountSettingsLoginEmailLabel}`}>Passkeys</div>
                      <div className={`fs-unmask ${styles.accountSettingsLoginEmailValue}`}>
                        {passkeys.length === 0 ? 'None' : `${passkeys.length} registered`}
                      </div>
                      <ul>
                        {passkeys.map((p) => (
                          <li key={p.id}>
                            {p.nickname || 'Passkey'}{' '}
                            <button type="button" aria-label={`Remove ${p.nickname || 'Passkey'}`} onClick={() => onRemovePasskey(p.id)}>
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className={styles.accountSettingsLoginEmailEdit}>
                      <button
                        type="button"
                        className={styles.accountSettingsLoginEmailEditButton}
                        onClick={onAddPasskey}
                      >
                        Add a passkey
                      </button>
                    </div>
                  </div>
                )}

                {/* Account security - MFA */}
                <h1 className={`fs-unmask ${styles.accountSettingsTitle}`}>Account security</h1>

                <div className={`fs-unmask ${styles.accountSettingsToggle}`}>
                  <div className={styles.accountSettingsToggleContainer}>
                    <MdLockOutline className={styles.accountSettingsToggleIcon} />
                    <div className={`fs-unmask ${styles.accountSettingsToggleLabel}`}>Multi-factor authentication</div>
                  </div>

                  <div className={styles.accountSettingToggleInput}>
                    <Switch
                      checked={isSelected === 1}
                      onChange={async (_, checked) => {
                        const newValue = checked ? 1 : 2;

                        if (userInfo.role?.name === UserRoleName.Patient) {
                          setShowConfirmPasswordModal(true);
                          setButtonNumber(newValue);
                          setPendingMfaToggle(newValue);
                        } else {
                          // Admins: instant apply
                          const prev = isSelected;
                          setIsSelected(newValue);
                          try {
                            setMfaLoading(true);
                            await manageMfa(Number(userInfo.id), {
                              selected_method: checked ? selectedMethod || 'EMAIL_MFA' : null,
                            });
                            setInitialValues({
                              isSelected: newValue,
                              selected_method: checked ? selectedMethod || 'EMAIL_MFA' : '',
                            });
                          } catch (err) {
                            displayErrorDetails(err);
                            setIsSelected(prev ?? 2);
                          } finally {
                            setMfaLoading(false);
                          }
                        }
                      }}
                      color="primary"
                      sx={isStaffPortalUser ? switchBlueSx : {
                        '& .MuiSwitch-switchBase.Mui-checked': { color: getPrimaryColor() },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#42a5f5' },
                      }}
                    />
                  </div>
                </div>

                {isSelected === 1 && (
                  <div style={{ width: '100%' }} className={styles.accountSettingsRow}>
                    <div className={cn(styles.rowItem, styles.details)}>
                      <MUISelectInput
                        label="Authentication code delivery method"
                        data-testid="preferredMethodDesktop"
                        errors={{}}
                        name="preferredMethodDesktop"
                        onChange={async (value: string) => {
                          if (!value) return;
                          const newMethod = value || 'EMAIL_MFA';
                          const prev = selectedMethod;
                          setSelectedMethod(newMethod);
                          try {
                            setMfaLoading(true);
                            await manageMfa(Number(userInfo.id), { selected_method: newMethod });
                            setInitialValues({ isSelected, selected_method: newMethod });
                          } catch (err) {
                            displayErrorDetails(err);
                            setSelectedMethod(prev);
                          } finally {
                            setMfaLoading(false);
                          }
                        }}
                        value={selectedMethod}
                        required
                        options={mfaOptionsList}
                        wrapperStyle={{ width: '100%' }}
                      />
                    </div>
                  </div>
                )}

                {/* Notification settings */}
                <div>
                  <h1 className={`fs-unmask ${styles.accountSettingsTitle}`}>Notification settings</h1>
                  <div className={`fs-unmask ${styles.accountSettingsText}`}>
                    We’ll send appointment reminders and other messages from our care team to your preferred method of
                    communication.
                  </div>
                </div>

                <div className={styles.accountSettingsToggle}>
                  <div className={styles.accountSettingsToggleContainer}>
                    <MdOutlineMailOutline className={styles.accountSettingsToggleIcon} />
                    <div className={`fs-unmask ${styles.accountSettingsToggleLabel}`}>Email</div>
                  </div>
                  <div className={styles.accountSettingToggleInput}>
                    <Switch
                      checked={!!userInfo?.email_opt_in}
                      onChange={() => {
                        updateNotificationSettings(!!userInfo.sms_opt_in, !userInfo.email_opt_in);
                      }}
                      color="primary"
                      sx={isStaffPortalUser ? switchBlueSx : {
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '$primary_color' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#42a5f5' },
                      }}
                    />
                  </div>
                </div>

                <div className={styles.accountSettingsToggle}>
                  <div className={styles.accountSettingsToggleContainer}>
                    <MdOutlinePhoneIphone className={styles.accountSettingsToggleIcon} />
                    <div className={`fs-unmask ${styles.accountSettingsToggleLabel}`}>SMS</div>
                  </div>
                  <div className={styles.accountSettingToggleInput}>
                    <Switch
                      checked={!!userInfo?.sms_opt_in}
                      onChange={() => {
                        updateNotificationSettings(!userInfo.sms_opt_in, !!userInfo.email_opt_in);
                      }}
                      color="primary"
                      sx={isStaffPortalUser ? switchBlueSx : {
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '$primary_color' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#42a5f5' },
                      }}
                    />
                  </div>
                </div>

                {noNotificationError && (
                  <div className={styles.accountSettingsAlert} role="alert">
                    <div className={`fs-unmask ${styles.accountSettingsAlertText}`}>
                      At least one notification method must be enabled
                    </div>
                  </div>
                )}

                {!isStaffPortalUser && (
                  <Button
                    type="button"
                    variant="contained"
                    className={`fs-unmask ${styles.formBtn}`}
                    onClick={onDeleteAccount}
                    style={{ marginTop: '0.5rem' }}
                  >
                    <MdDeleteOutline />
                    Delete Account
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Confirm password for MFA change (Patients only) */}
        <ConfirmPasswordModal
          isOpen={showConfirmPasswordModal}
          onClose={onCloseConfirmPasswordModal}
          email={userInfo.email}
          buttonNumber={buttonNumber}
          isSelected={isSelected}
          setIsSelected={setIsSelected}
        />
      </Container>
    );
  }

  // ---- DESKTOP MODAL LAYOUT ----
  if (loadingState && !isMobile) {
    return (
      <Modal
        isOpen={isOpen}
        onRequestClose={closeModal}
        style={customStylesDashboardCustom()}
        contentLabel="Modal"
        ariaHideApp={false}
      >
        <div
          className={cn(styles.accountSettingsCard)}
          style={{ height: '532px', width: isStaffPortalUser ? '520px' : '452px' }}
        >
          <Loading dotted />
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      style={customStylesDashboardCustom(showConfirmPasswordModal ? '0' : '1')}
      contentLabel="Modal"
      ariaHideApp={false}
    >
      <div className={cn(styles.accountSettingsCard)}>
        <button
          type="button"
          className={`fs-unmask ${styles.modalClose}`}
          data-micromodal-close=""
          onClick={closeModal}
          aria-label="Close modal"
        >
          <AiOutlineClose className={styles.icon} />
        </button>
        {(!isStaffPortalUser || isMobile) && (
          <button type="button" className={styles.backButton} onClick={closeModal} aria-label="Back">
            <MdKeyboardArrowLeft />
            Back
          </button>
        )}

        <div className={styles.accountSettingsContainer}>
          <h1 className={`fs-unmask ${styles.accountSettingsTitle}`}>Account settings</h1>

          {/* Login email */}
          <div className={styles.accountSettingsLoginEmail}>
            <div className={styles.accountSettingsLoginEmailContainer}>
              <div className={`fs-unmask ${styles.accountSettingsLoginEmailLabel}`}>Login email</div>
              <div className={`fs-exclude ${styles.accountSettingsLoginEmailValue}`}>{userInfo?.email}</div>
            </div>
            {/* <div className={`fs-unmask ${styles.accountSettingsLoginEmailEdit}`}>
              <button
                type="button"
                className={`fs-unmask ${styles.accountSettingsLoginEmailEditButton}`}
                onClick={onChangeEmail}
              >
                Edit
              </button>
            </div> */}
          </div>

          {/* Password */}
          <div className={styles.accountSettingsLoginEmail}>
            <div className={styles.accountSettingsLoginEmailContainer}>
              <div className={`fs-unmask ${styles.accountSettingsLoginEmailLabel}`}>Password</div>
              <div className={`fs-unmask ${styles.accountSettingsLoginEmailValue}`}>********</div>
            </div>
            <div className={`fs-unmask  ${styles.accountSettingsLoginEmailEdit}`}>
              <button
                type="button"
                className={`fs-unmask ${styles.accountSettingsLoginEmailEditButton}`}
                onClick={onChangePassword}
              >
                Edit
              </button>
            </div>
          </div>

          {/* Passkeys */}
          {config.features.passkey && browserSupportsWebAuthn() && (
            <div className={styles.accountSettingsLoginEmail}>
              <div className={styles.accountSettingsLoginEmailContainer}>
                <div className={`fs-unmask ${styles.accountSettingsLoginEmailLabel}`}>Passkeys</div>
                <div className={`fs-unmask ${styles.accountSettingsLoginEmailValue}`}>
                  {passkeys.length === 0 ? 'None' : `${passkeys.length} registered`}
                </div>
                <ul>
                  {passkeys.map((p) => (
                    <li key={p.id}>
                      {p.nickname || 'Passkey'}{' '}
                      <button type="button" aria-label={`Remove ${p.nickname || 'Passkey'}`} onClick={() => onRemovePasskey(p.id)}>
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={styles.accountSettingsLoginEmailEdit}>
                <button
                  type="button"
                  className={styles.accountSettingsLoginEmailEditButton}
                  onClick={onAddPasskey}
                >
                  Add a passkey
                </button>
              </div>
            </div>
          )}

          {/* Account security - MFA */}
          <h1 className={`fs-unmask ${styles.accountSettingsTitle}`}>Account security</h1>

          <div className={`fs-unmask ${styles.accountSettingsToggle}`}>
            <div className={styles.accountSettingsToggleContainer}>
              <MdLockOutline className={styles.accountSettingsToggleIcon} />
              <div className={`fs-unmask ${styles.accountSettingsToggleLabel}`}>Multi-factor authentication</div>
            </div>

            <div className={styles.accountSettingToggleInput}>
              <Switch
                checked={isSelected === 1}
                onChange={async (_, checked) => {
                  const newValue = checked ? 1 : 2;

                  if (userInfo.role?.name === UserRoleName.Patient) {
                    setShowConfirmPasswordModal(true);
                    setButtonNumber(newValue);
                    setPendingMfaToggle(newValue);
                  } else {
                    // Admins: instant apply
                    const prev = isSelected;
                    setIsSelected(newValue);
                    try {
                      setMfaLoading(true);
                      await manageMfa(Number(userInfo.id), {
                        selected_method: checked ? selectedMethod || 'EMAIL_MFA' : null,
                      });
                      setInitialValues({
                        isSelected: newValue,
                        selected_method: checked ? selectedMethod || 'EMAIL_MFA' : '',
                      });
                    } catch (err) {
                      displayErrorDetails(err);
                      setIsSelected(prev ?? 2);
                    } finally {
                      setMfaLoading(false);
                    }
                  }
                }}
                color="primary"
                sx={isStaffPortalUser ? switchBlueSx : {
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '$primary_color' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#42a5f5' },
                }}
              />
            </div>
          </div>

          {isSelected === 1 && (
            <div style={{ width: '100%' }} className={styles.accountSettingsRow}>
              <div className={cn(styles.rowItem, styles.details)}>
                <MUISelectInput
                  label="Authentication code delivery method"
                  data-testid="preferredMethodDesktop"
                  errors={{}}
                  name="preferredMethodDesktop"
                  onChange={async (value: string) => {
                    if (!value) return;
                    const newMethod = value || 'EMAIL_MFA';
                    const prev = selectedMethod;
                    setSelectedMethod(newMethod);
                    try {
                      setMfaLoading(true);
                      await manageMfa(Number(userInfo.id), { selected_method: newMethod });
                      setInitialValues({ isSelected, selected_method: newMethod });
                    } catch (err) {
                      displayErrorDetails(err);
                      setSelectedMethod(prev);
                    } finally {
                      setMfaLoading(false);
                    }
                  }}
                  value={selectedMethod}
                  required
                  options={mfaOptionsList}
                  wrapperStyle={{ width: '100%' }}
                />
              </div>
            </div>
          )}

          {/* Notification settings */}
          <div>
            <h1 className={`fs-unmask ${styles.accountSettingsTitle}`}>Notification settings</h1>
            <div className={`fs-unmask ${styles.accountSettingsText}`}>
              We’ll send appointment reminders and other messages from our care team to your preferred method of
              communication.
            </div>
          </div>

          <div className={styles.accountSettingsToggle}>
            <div className={styles.accountSettingsToggleContainer}>
              <MdOutlineMailOutline className={styles.accountSettingsToggleIcon} />
              <div className={`fs-unmask ${styles.accountSettingsToggleLabel}`}>Email</div>
            </div>
            <div className={styles.accountSettingToggleInput}>
              <Switch
                checked={!!userInfo?.email_opt_in}
                onChange={() => {
                  updateNotificationSettings(!!userInfo.sms_opt_in, !userInfo.email_opt_in);
                }}
                color="primary"
                sx={isStaffPortalUser ? switchBlueSx : {
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '$primary_color' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#42a5f5' },
                }}
              />
            </div>
          </div>

          <div className={styles.accountSettingsToggle}>
            <div className={styles.accountSettingsToggleContainer}>
              <MdOutlinePhoneIphone className={styles.accountSettingsToggleIcon} />
              <div className={`fs-unmask ${styles.accountSettingsToggleLabel}`}>SMS</div>
            </div>
            <div className={styles.accountSettingToggleInput}>
              <Switch
                checked={!!userInfo?.sms_opt_in}
                onChange={() => {
                  updateNotificationSettings(!userInfo.sms_opt_in, !!userInfo.email_opt_in);
                }}
                color="primary"
                sx={isStaffPortalUser ? switchBlueSx : {
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '$primary_color' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#42a5f5' },
                }}
              />
            </div>
          </div>

          {noNotificationError && (
            <div className={styles.accountSettingsAlert} role="alert">
              <div className={`fs-unmask ${styles.accountSettingsAlertText}`}>
                At least one notification method must be enabled
              </div>
            </div>
          )}

          {!isStaffPortalUser && (
            <Button type="button" variant="contained" className={`fs-unmask ${styles.formBtn}`} onClick={onDeleteAccount}>
              <MdDeleteOutline />
              Delete Account
            </Button>
          )}
        </div>
      </div>

      {/* Confirm password for MFA change (Patients only) */}
      <ConfirmPasswordModal
        isOpen={showConfirmPasswordModal}
        onClose={onCloseConfirmPasswordModal}
        email={userInfo.email}
        buttonNumber={buttonNumber}
        isSelected={isSelected}
        setIsSelected={setIsSelected}
      />
    </Modal>
  );
};

Modal.setAppElement('#root');

export default AccountSettingsModal;
